import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'

import { Schedule } from '@/core/api/entities/schedule'
import permissionService, {
	PERMISSIONS,
} from '@/core/api/services/permissionService'
import { useContentCategories } from '@/core/hooks/misc/useContentCategories'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import {
	useAssignedSchedules,
	useInUseSchedules,
	useFavoriteSchedules,
	useSchedules,
	useToggleScheduleFavorite,
} from '@/core/hooks/useSchedule'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { useTranslation } from '@/core/i18n/useTranslation'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

import ViewAllSchedulesPageUI from './ViewAllSchedulesPageUI'

type ScheduleType = 'assigned' | 'favorites' | 'all' | 'in-use'

const ViewAllSchedulesPage: React.FC = () => {
	const { t } = useTranslation('common')
	const { showNotification } = useNotificationContext()
	const { type } = useParams<{ type: ScheduleType }>()
	const [searchParams] = useSearchParams()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [searchQuery, setSearchQuery] = useState('')
	const debouncedSearchQuery = useDebounce(searchQuery, 500)
	const [currentPage, setCurrentPage] = useState(1)
	const [allSchedules, setAllSchedules] = useState<Schedule[]>([])
	const processedPagesRef = useRef<Set<number>>(new Set())
	const [isLoadingMorePages, setIsLoadingMorePages] = useState(false)

	// Get filter values from URL params
	const contentCategoryFilter = searchParams.get('content_category_ids')
	const titleFromQuery = searchParams.get('title')

	// Fetch content categories to get names
	const { data: contentCategories = [] } = useContentCategories()

	// Get section name based on filter
	const sectionName = useMemo(() => {
		if (contentCategoryFilter) {
			const category = contentCategories.find(
				(c) => c.id === Number(contentCategoryFilter)
			)
			return category ? category.getLocalizedName('en-US') : null
		}
		return null
	}, [contentCategoryFilter, contentCategories])

	// Scroll to top when entering the page or filter changes
	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}, [type, contentCategoryFilter])

	// Validate type and redirect if invalid
	useEffect(() => {
		if (!type || !['assigned', 'favorites', 'all', 'in-use'].includes(type)) {
			navigate('/schedules', { replace: true })
		}
	}, [type, navigate])

	// Fetch schedules based on type
	const assignedQuery = useAssignedSchedules({
		config: useAccountConfig({
			params: {
				per_page: 10,
				page: currentPage,
				public: false,
			},
		}),
		options: {
			enabled: type === 'assigned',
		},
	})

	const favoritesQuery = useFavoriteSchedules({
		config: useAccountConfig({
			params: {
				per_page: 10,
				page: currentPage,
			},
		}),
		options: {
			enabled: type === 'favorites',
		},
	})

	const inUseQuery = useInUseSchedules({
		config: useAccountConfig({
			params: {
				per_page: 10,
				page: currentPage,
				public: false,
			},
		}),
		options: {
			enabled: type === 'in-use',
		},
	})

	// Always fetch favorite schedules to check favorite status (for all types)
	const { data: allFavoriteSchedulesData, refetch: refetchAllFavorites } =
		useFavoriteSchedules({
			config: useAccountConfig({
				params: {
					per_page: 100, // Fetch enough to cover most cases
				},
			}),
			options: {
				enabled: true, // Always enabled to check favorite status
			},
		})

	// Build query params for all schedules (including filters)
	const allSchedulesParams = useMemo(() => {
		const params: Record<string, number | boolean> = {
			per_page: 10,
			page: currentPage,
			public: true,
		}

		// Add filter params if present
		if (contentCategoryFilter) {
			params.content_category_ids = Number(contentCategoryFilter)
		}

		return params
	}, [currentPage, contentCategoryFilter])

	const allSchedulesQuery = useSchedules({
		config: useAccountConfig({
			params: {
				...allSchedulesParams,
			},
		}),
		options: {
			enabled: type === 'all',
		},
	})

	// Get the active query based on type
	const activeQuery =
		type === 'assigned'
			? assignedQuery
			: type === 'favorites'
				? favoritesQuery
				: type === 'in-use'
					? inUseQuery
					: allSchedulesQuery

	const isLoading = activeQuery.isLoading
	const pagination = activeQuery.data?.meta?.pagination

	// Determine which schedules to show
	const showSearchResults = debouncedSearchQuery.length > 0

	// Accumulate schedules from all loaded pages
	useEffect(() => {
		if (showSearchResults) {
			// Reset accumulated schedules when searching
			setAllSchedules([])
			processedPagesRef.current = new Set()
			setCurrentPage(1)
			setIsLoadingMorePages(false)
			return
		}

		const newSchedules = activeQuery.data?.data || []
		if (
			newSchedules.length > 0 &&
			!processedPagesRef.current.has(currentPage)
		) {
			// Process this page's data only if we haven't processed it yet
			if (currentPage === 1) {
				// First page - replace all
				setAllSchedules(newSchedules)
			} else {
				// Subsequent pages - append
				setAllSchedules((prevSchedules) => {
					// Avoid duplicates by checking IDs
					const existingIds = new Set(prevSchedules.map((s) => s.id))
					const uniqueNewSchedules = newSchedules.filter(
						(s) => !existingIds.has(s.id)
					)
					return [...prevSchedules, ...uniqueNewSchedules]
				})
			}
			// Mark this page as processed
			processedPagesRef.current.add(currentPage)
			setIsLoadingMorePages(false)
		}
	}, [activeQuery.data, currentPage, showSearchResults])

	// Filter schedules based on search
	const filteredSchedules = useMemo(() => {
		if (!debouncedSearchQuery.trim()) {
			return allSchedules
		}
		const query = debouncedSearchQuery.toLowerCase()
		return allSchedules.filter(
			(schedule) =>
				schedule.title.toLowerCase().includes(query) ||
				schedule.description?.toLowerCase().includes(query)
		)
	}, [allSchedules, debouncedSearchQuery])

	const schedules = showSearchResults ? filteredSchedules : allSchedules
	const isLoadingMore = isLoadingMorePages

	// Create a Set of favorite schedule IDs for quick lookup
	const favoriteScheduleIds = useMemo(() => {
		const favoriteSchedules = allFavoriteSchedulesData?.data || []
		return new Set(favoriteSchedules.map((schedule) => schedule.id))
	}, [allFavoriteSchedulesData])

	// Mutation for toggling schedule favorite
	const toggleFavoriteMutation = useToggleScheduleFavorite({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				activeQuery.refetch()
				refetchAllFavorites()
				queryClient.invalidateQueries({ queryKey: ['schedules-assigned'] })
				queryClient.invalidateQueries({ queryKey: ['schedules-in-use'] })
				queryClient.invalidateQueries({ queryKey: ['schedules-favorites'] })
				showNotification({
					message: t('entities.music.favorite_playlists'),
					type: 'success',
				})
			},
			onError: () => {
				showNotification({
					message: 'Failed to update favorites',
					type: 'error',
				})
			},
		},
	})

	// Handle favorite click
	const handleFavoriteClick = useCallback(
		(schedule: Schedule, isFavoritesSection: boolean) => {
			let favorite: boolean
			if (isFavoritesSection) {
				// In favorites section, always unfavorite
				favorite = false
			} else {
				// In other sections, toggle based on current status
				const isCurrentlyFavorite = favoriteScheduleIds.has(schedule.id)
				favorite = !isCurrentlyFavorite
			}

			toggleFavoriteMutation.mutate({
				scheduleId: String(schedule.id),
				favorite,
			})
		},
		[toggleFavoriteMutation, favoriteScheduleIds]
	)

	// Schedule click handler
	const handleScheduleClick = useCallback(
		(schedule: Schedule) => {
			navigate(`/schedules/${schedule.id}`)
		},
		[navigate]
	)

	// Modal handlers
	const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
	const [selectedScheduleForAssign, setSelectedScheduleForAssign] = useState<{
		id: string
		name: string
	} | null>(null)

	const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
	const [selectedScheduleForRemove, setSelectedScheduleForRemove] = useState<{
		id: string
		name: string
		description?: string
	} | null>(null)

	const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
	const [selectedScheduleForDuplicate, setSelectedScheduleForDuplicate] =
		useState<{
			id: string
			name: string
			description?: string
		} | null>(null)

	const handleAssign = useCallback((scheduleId: number, name: string) => {
		setSelectedScheduleForAssign({
			id: String(scheduleId),
			name,
		})
		setIsAssignModalOpen(true)
	}, [])

	const canCreateSchedules = permissionService.currentUserHasAnyPermission([
		PERMISSIONS.CREATE_SCHEDULES,
		PERMISSIONS.MANAGE_CHILDREN_SCHEDULES,
		PERMISSIONS.MANAGE_OWN_LEVEL_SCHEDULES,
	])
	const canEditSchedule = permissionService.currentUserHasAnyPermission([
		PERMISSIONS.MANAGE_CHILDREN_SCHEDULES,
		PERMISSIONS.MANAGE_OWN_LEVEL_SCHEDULES,
		PERMISSIONS.UPDATE_SCHEDULES,
	])
	const canDeleteSchedule = permissionService.currentUserHasAnyPermission([
		PERMISSIONS.MANAGE_CHILDREN_SCHEDULES,
		PERMISSIONS.MANAGE_OWN_LEVEL_SCHEDULES,
		PERMISSIONS.DESTROY_SCHEDULES,
	])

	const handleDuplicate = useCallback(
		(scheduleId: number, name: string, description: string) => {
			if (!canCreateSchedules) {
				return
			}

			setSelectedScheduleForDuplicate({
				id: String(scheduleId),
				name,
				description,
			})
			setIsDuplicateModalOpen(true)
		},
		[canCreateSchedules]
	)

	const handleEdit = useCallback(
		(scheduleId: number) => {
			navigate(`/schedules/${scheduleId}?mode=edit`)
		},
		[navigate]
	)

	const handleRemove = useCallback(
		(scheduleId: number, name: string, description: string) => {
			setSelectedScheduleForRemove({
				id: String(scheduleId),
				name,
				description,
			})
			setIsRemoveModalOpen(true)
		},
		[]
	)

	// Handle load more (infinite scroll)
	const handleLoadMore = useCallback(() => {
		if (!pagination || currentPage >= pagination.total_pages || isLoading) {
			return
		}
		const nextPage = currentPage + 1
		setIsLoadingMorePages(true)
		setCurrentPage(nextPage)
	}, [currentPage, pagination, isLoading])

	// Get page title based on type and filters
	const getPageTitle = () => {
		if (!type) return t('navigation.schedule')
		if (titleFromQuery) return titleFromQuery
		if (contentCategoryFilter && sectionName) return sectionName
		switch (type) {
			case 'assigned':
				return t('entities.schedule.custom_schedules')
			case 'in-use':
				return t('entities.schedule.assigned_schedules')
			case 'favorites':
				return t('entities.music.favorites')
			case 'all':
				// If there's a filter, show the section name instead of "All schedules"
				return sectionName || t('entities.schedule.all_schedules')
			default:
				return t('navigation.schedule')
		}
	}

	// Don't render if type is invalid
	if (!type || !['assigned', 'favorites', 'all', 'in-use'].includes(type)) {
		return null
	}

	// Determine if schedules are editable (not public)
	const isEditable =
		type === 'assigned' || type === 'favorites' || type === 'in-use'

	return (
		<ViewAllSchedulesPageUI
			title={getPageTitle()}
			searchQuery={searchQuery}
			onSearchChange={setSearchQuery}
			schedules={schedules}
			isLoading={isLoading}
			showSearchResults={showSearchResults}
			onScheduleClick={handleScheduleClick}
			onFavoriteClick={handleFavoriteClick}
			hasMore={pagination ? currentPage < pagination.total_pages : false}
			pagination={pagination}
			currentPage={currentPage}
			isLoadingMore={isLoadingMore}
			onLoadMore={handleLoadMore}
			scheduleType={type}
			isEditable={isEditable}
			onAssign={handleAssign}
			onDuplicate={canCreateSchedules ? handleDuplicate : undefined}
			onEdit={canEditSchedule ? handleEdit : undefined}
			onRemove={canDeleteSchedule ? handleRemove : undefined}
			// Modal props
			isAssignModalOpen={isAssignModalOpen}
			onAssignModalChange={setIsAssignModalOpen}
			selectedScheduleForAssign={selectedScheduleForAssign}
			isRemoveModalOpen={isRemoveModalOpen}
			onRemoveModalChange={setIsRemoveModalOpen}
			selectedScheduleForRemove={selectedScheduleForRemove}
			isDuplicateModalOpen={isDuplicateModalOpen}
			onDuplicateModalChange={setIsDuplicateModalOpen}
			selectedScheduleForDuplicate={selectedScheduleForDuplicate}
		/>
	)
}

export default ViewAllSchedulesPage
